import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ListResponse } from '../../../shared/utils/types.elements';
import { apiUrlEvents } from '../constants/apiUrl.events';
import { ConstNumber } from '../../../shared/constants/number.constant';
import {
    Event,
    EventForm,
    EventFormRequest,
    EventFormWarehouse,
    EventV2,
} from '../constants/types.events';
import {
    formatChangeDateTime,
    formatDateAndTime,
    getTimeZone,
    mapFormDataFields,
} from '../../../shared/utils/helper';
import {
    ApiParamEnum,
    HttpMethods,
} from '../../../shared/constants/application.constant';
import {
    BusinessLevel3Request,
    BusinessLevel3Response,
    Level3Response,
    VenueDetailsResponse,
} from '../../businessInfo/utils/types.businessInfo';
import { getDataRequest, getUpdatedValues } from '../utils/utils';

// Define a service using a base URL and expected endpoints
export const eventsApi = createApi({
    reducerPath: 'events',
    baseQuery: interceptorMiddleware,
    tagTypes: ['events'],
    endpoints: (builder) => ({
        getEvents: builder.query<
            ListResponse<Event>,
            { page: number; isClaimed: string }
        >({
            query: ({ page, isClaimed }) => ({
                url: `${apiUrlEvents.EVENTS}?limit=${
                    ApiParamEnum.LIMIT
                }&offset=${
                    (page - ConstNumber.VALUE_1) * ApiParamEnum.OFFSET
                }&is_claimed=${isClaimed}`,
                method: HttpMethods.GET,
            }),
            providesTags: ['events'],
        }),
        createEvent: builder.mutation({
            query: (data: EventForm) => {
                const eventPayload = {
                    name: data.name,
                    description: data.description ?? '',
                    address: data.address,
                    categories_list: JSON.stringify(data.categories_list),
                    start_datetime: formatDateAndTime(
                        data.start_date,
                        data.start_time
                    ),
                    end_datetime: formatDateAndTime(
                        data.end_date,
                        data.end_time
                    ),
                    price_range: data.price_range as any,
                    ticket_url: data.ticket_url as any,
                    document:
                        typeof data?.document === 'string'
                            ? data?.document
                            : data?.s3MediaUrl,
                    display_start_datetime: formatChangeDateTime(
                        data.start_date,
                        data.start_time
                    ),
                    display_end_datetime: formatChangeDateTime(
                        data.end_date,
                        data.end_time
                    ),
                };
                return {
                    url: apiUrlEvents.EVENTS,
                    method: HttpMethods.POST,
                    body: eventPayload,
                };
            },
        }),
        getEventDetails: builder.query<VenueDetailsResponse, { id: number }>({
            query: ({ id }) => ({
                url: `${apiUrlEvents.EVENTS_LEVEL_TWO}${id}/`,
                method: 'GET',
            }),
            providesTags: ['events'],
        }),
        getEventLevel3: builder.query<Level3Response, { id: number }>({
            query: ({ id }) => ({
                url: `${apiUrlEvents.EVENTS_LEVEL_THREE}${id}/`,
                method: 'GET',
            }),
            providesTags: ['events'],
        }),
        updateEventLevel3: builder.mutation<
            BusinessLevel3Response,
            { id: number; data: BusinessLevel3Request }
        >({
            query: ({ id, data }) => ({
                url: `${apiUrlEvents.EVENTS_LEVEL_THREE}?event_id=${id}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['events'],
        }),
        getEventById: builder.query<EventV2, number>({
            query: (id: number) => ({
                url: `${apiUrlEvents.EVENTS}${id}/`,
                method: HttpMethods.GET,
            }),
            providesTags: [],
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
        updateEvent: builder.mutation<
            {
                message: string;
            },
            {
                data: EventForm;
                initialValues: EventFormRequest;
                isClaimedEvent: boolean;
            }
        >({
            query: ({ data, initialValues, isClaimedEvent }) => {
                const dataRequest: EventFormRequest = getDataRequest(data);
                const changedValues = getUpdatedValues(
                    initialValues,
                    dataRequest
                );
                if ('document' in changedValues) {
                    changedValues['document'] =
                        changedValues['s3MediaUrl'] || '';
                }
                if (isClaimedEvent) {
                    changedValues['is_published'] = true;
                }
                delete changedValues['s3MediaUrl'];
                return {
                    url: `${apiUrlEvents.EVENTS}${data.id}/`,
                    method: HttpMethods.PATCH,
                    body: changedValues,
                };
            },
        }),
        updateWarehouseEvent: builder.mutation<
            {
                message: string;
            },
            { data: EventFormWarehouse; initialValues: EventFormRequest }
        >({
            query: ({ data, initialValues }) => {
                const dataRequest: EventFormRequest = getDataRequest(data);
                const changedValues = getUpdatedValues(
                    initialValues,
                    dataRequest
                );
                if ('document' in changedValues) {
                    changedValues['document'] =
                        changedValues['s3MediaUrl'] || '';
                }
                delete changedValues['s3MediaUrl'];
                // Since date is disabled, we are making sure to not send anything realted to date
                delete changedValues['display_end_datetime'];
                delete changedValues['display_start_datetime'];
                delete changedValues['end_datetime'];
                delete changedValues['start_datetime'];
                return {
                    url: `${apiUrlEvents.EVENTS}${data.id}/`,
                    method: HttpMethods.PATCH,
                    body: { ...changedValues, is_published: true },
                };
            },
        }),
        deleteEvent: builder.mutation<{ message: string }, { id: number }>({
            query: ({ id }) => {
                return {
                    url: `${apiUrlEvents.EVENTS}${id}/`,
                    method: HttpMethods.DELETE,
                };
            },
        }),
        eventUpload: builder.mutation({
            query: (data: FormData) => {
                return {
                    url: `${
                        apiUrlEvents.UPLOAD_EVENTS
                    }?time_zone=${getTimeZone()}`,
                    method: HttpMethods.POST,
                    body: data,
                };
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetEventsQuery,
    useCreateEventMutation,
    useGetEventDetailsQuery,
    useGetEventLevel3Query,
    useUpdateEventLevel3Mutation,
    useGetEventByIdQuery,
    useUpdateEventMutation,
    useDeleteEventMutation,
    useEventUploadMutation,
    useUpdateWarehouseEventMutation,
} = eventsApi;
